// src/context/DonateContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import DonationModal from "../components/DonationModal";
import { API_BASE } from "../config";

const DonateCtx = createContext(null);

export function DonateProvider({ children }) {
  const [open, setOpen] = useState(false);

  // Modal-controlled state (owned here so any page can open with prefs)
  const [donationAmount, setDonationAmount] = useState(25);
  const [dedicatedTo, setDedicatedTo] = useState("");
  const [startOnContact, setStartOnContact] = useState(false);

  // Consumers can use this to refresh UI (e.g., DonationHighlights)
  const [refreshTick, setRefreshTick] = useState(0);

  const openDonate = useCallback(
    ({ amount, honoree, startOnContact: startOnContactFlag } = {}) => {
      if (typeof amount === "number") setDonationAmount(amount);
      if (typeof honoree === "string") setDedicatedTo(honoree);
      setStartOnContact(!!startOnContactFlag);
      setOpen(true);
    },
    []
  );

  const closeDonate = useCallback(() => {
    setOpen(false);
    setStartOnContact(false);
  }, []);

  const handleDonationSaved = useCallback(async (dataFromModal) => {
    const payload = { ...dataFromModal, dedicatedTo };
    try {
      const res = await fetch(`${API_BASE}/api/Payments/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());

      // Reset for next time
      setDedicatedTo("");
      setDonationAmount(25);
      setOpen(false);
      setStartOnContact(false);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      console.error(err);
      alert("There was an error saving your gift. Please contact support.");
    }
  }, [dedicatedTo]);

  return (
    <DonateCtx.Provider
      value={{
        openDonate,
        closeDonate,
        donationAmount,
        setDonationAmount,
        dedicatedTo,
        setDedicatedTo,
        refreshTick,
      }}
    >
      {children}

      <DonationModal
        open={open}
        onClose={closeDonate}
        setDonationAmount={setDonationAmount}
        donationAmount={donationAmount}
        setDedicatedTo={setDedicatedTo}
        startOnContact={startOnContact}
        onDonationSaved={handleDonationSaved}
      />
    </DonateCtx.Provider>
  );
}

export function useDonate() {
  const ctx = useContext(DonateCtx);
  if (!ctx) throw new Error("useDonate must be used within DonateProvider");
  return ctx;
}
